import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import type { RecipientOverlap } from "../../../api/inquiryDraftApi"
import { inquiryStatusLabel } from "../../../api/supplierInquiriesApi"
import { formatDate } from "../../../utils/date"

/** One inquiry the send was refused for, with the other inquiries to its address. */
export interface RecipientOverlapEntry {
  inquiryId: string
  supplierName: string
  overlaps: RecipientOverlap[]
}

interface RecipientOverlapDialogProps {
  /** "single" names one inquiry; "bulk" lists every inquiry a batch was refused for. */
  mode: "single" | "bulk"
  entries: RecipientOverlapEntry[]
  /** Send anyway: the caller retries with the other inquiries' ids as acknowledged. */
  onConfirm: () => void
  /** Bulk only: leave the flagged inquiries as drafts and carry on with the rest. */
  onSkip?: () => void
  onCancel: () => void
  isLoading?: boolean
}

/**
 * Shown when a send comes back 409 RECIPIENT_ALREADY_INQUIRED: another supplier's
 * inquiry goes to the same address (often two companies of one group with one
 * contact, TUR-1812). Nothing was sent for those inquiries yet. Only a hint: the user
 * can send anyway.
 */
export function RecipientOverlapDialog({
  mode,
  entries,
  onConfirm,
  onSkip,
  onCancel,
  isLoading = false,
}: RecipientOverlapDialogProps) {
  const { translate } = useTranslation()
  const bulk = mode === "bulk"

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isLoading) onCancel()
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {bulk
              ? translate("MC_INQ_OVERLAP_DIALOG_BULK_TITLE", {
                  count: entries.length,
                })
              : translate("MC_INQ_OVERLAP_DIALOG_TITLE")}
          </DialogTitle>
          <DialogDescription>
            {bulk
              ? translate("MC_INQ_OVERLAP_DIALOG_BULK_DESC")
              : translate("MC_INQ_OVERLAP_DIALOG_DESC", {
                  count: otherInquiryCount(entries),
                })}
          </DialogDescription>
        </DialogHeader>

        {bulk ? (
          // One block per shared address: the drafts of this send once, and every
          // other inquiry to that address once, instead of repeating both for each
          // held draft.
          <ul className="flex flex-col gap-3">
            {bulkByAddress(entries).map(({ address, inSend, others }) => (
              <li
                key={address}
                className="flex flex-col gap-1.5 rounded-md border border-grey4 bg-grey3 p-3"
              >
                <div className="text-[13px] font-medium break-all text-white">
                  {address}
                </div>
                <div className="text-[12.5px] text-grey5">
                  {translate("MC_INQ_OVERLAP_IN_SEND", {
                    suppliers: inSend.join(", "),
                  })}
                </div>
                {others.length > 0 && (
                  <>
                    <div className="mt-1 text-[11.5px] text-grey5">
                      {translate("MC_INQ_OVERLAP_OTHERS")}
                    </div>
                    <OverlapList overlaps={others} />
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <ul className="flex flex-col gap-4">
            {entries.map((entry) => (
              <li key={entry.inquiryId} className="flex flex-col gap-2">
                {groupByAddress(entry.overlaps).map(([address, others]) => (
                  <div
                    key={address}
                    className="flex flex-col gap-1.5 rounded-md border border-grey4 bg-grey3 p-3"
                  >
                    <div className="text-[11.5px] break-all text-grey5">
                      {translate("MC_INQ_OVERLAP_ALSO", { address })}
                    </div>
                    <OverlapList overlaps={others} />
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}

        {/* Wraps instead of widening the dialog: the German labels are long. */}
        <DialogFooter className="flex-wrap gap-2 sm:space-x-0">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            {translate("MC_DECL_BTN_CANCEL")}
          </Button>
          {bulk && onSkip && (
            <Button variant="outline" onClick={onSkip} disabled={isLoading}>
              {translate("MC_INQ_OVERLAP_SKIP")}
            </Button>
          )}
          <Button onClick={onConfirm} disabled={isLoading}>
            {isLoading && <Loader2 size={14} className="animate-spin" />}
            {translate(
              bulk
                ? "MC_NOTE_WARN_SEND_ALL_ANYWAY"
                : "MC_NOTE_WARN_SEND_ANYWAY",
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The other inquiries, one line each: supplier · reference · status · sent date. */
function OverlapList({ overlaps }: { overlaps: RecipientOverlap[] }) {
  const { translate } = useTranslation()
  return (
    <ul className="flex flex-col gap-1">
      {overlaps.map((other) => (
        <li key={other.inquiryId} className="text-[13px] text-white">
          <span className="font-medium">{other.supplierName ?? "—"}</span>
          {" · "}
          {[
            other.reference,
            inquiryStatusLabel(other.status, translate),
            other.sentAt
              ? translate("MC_INQ_OVERLAP_SENT_ON", {
                  date: formatDate(other.sentAt),
                })
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </li>
      ))}
    </ul>
  )
}

/** Per shared address: the held drafts of this send that go there, and every other
 *  inquiry to it once. A held draft also shows up as another's overlap; it is listed
 *  under "in this send", not again as another inquiry. */
function bulkByAddress(entries: RecipientOverlapEntry[]): {
  address: string
  inSend: string[]
  others: RecipientOverlap[]
}[] {
  const heldIds = new Set(entries.map((entry) => entry.inquiryId))
  const byAddress = new Map<
    string,
    { inSend: string[]; others: Map<string, RecipientOverlap> }
  >()
  for (const entry of entries) {
    for (const overlap of entry.overlaps) {
      const group = byAddress.get(overlap.address) ?? {
        inSend: [] as string[],
        others: new Map<string, RecipientOverlap>(),
      }
      if (!group.inSend.includes(entry.supplierName)) {
        group.inSend.push(entry.supplierName)
      }
      if (!heldIds.has(overlap.inquiryId)) {
        group.others.set(overlap.inquiryId, overlap)
      }
      byAddress.set(overlap.address, group)
    }
  }
  return [...byAddress].map(([address, { inSend, others }]) => ({
    address,
    inSend,
    others: [...others.values()],
  }))
}

/** How many different other inquiries share an address with the held ones. */
function otherInquiryCount(entries: RecipientOverlapEntry[]): number {
  // Per address, since the text speaks of "the same address": a draft with two To
  // addresses that each have one other inquiry still reads "an inquiry".
  const byAddress = new Map<string, Set<string>>()
  for (const overlap of entries.flatMap((entry) => entry.overlaps)) {
    const ids = byAddress.get(overlap.address) ?? new Set<string>()
    ids.add(overlap.inquiryId)
    byAddress.set(overlap.address, ids)
  }
  return Math.max(0, ...[...byAddress.values()].map((ids) => ids.size))
}

/** The other inquiries per shared address, in the order the server listed them. */
function groupByAddress(
  overlaps: RecipientOverlap[],
): [string, RecipientOverlap[]][] {
  const byAddress = new Map<string, RecipientOverlap[]>()
  for (const overlap of overlaps) {
    byAddress.set(overlap.address, [
      ...(byAddress.get(overlap.address) ?? []),
      overlap,
    ])
  }
  return [...byAddress]
}
